import { FC, useEffect, useState } from "react";

import { Box } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";

import { FullWidthVStack } from "@components/common";
import EmailInput from "@components/EmailInput";
import Icon from "@components/Icon";
import Link from "@components/Link";
import Message, { useMessage } from "@components/Message";
import PhoneInput from "@components/PhoneInput";
import TextInput from "@components/TextInput";
import { Mobile } from "@lib/media";
import { useRouter } from "@lib/router";
import { LockerInput } from "@modules/locker-search";
import { DOMAIN } from "@utils/constants";

import { OrderFields, ORDER_MESSAGE_ZONE, ORDER_FIELDS_MAX_LENGTHS } from "../../constants";
import useOrderFormik from "../../hooks/useOrderFormik";
import SectionTitle from "../common/SectionTitle";

import SenderPhoneNotificationMessage from "../common/SenderPhoneNotificationMessage";
import { findCountryCode } from "@components/PhoneInput/utils/findCountryCode";

type TSenderDetailsProps = {
  onOpenLockersMap: () => void;
};

const SenderDetails: FC<TSenderDetailsProps> = ({ onOpenLockersMap }) => {
  const { t } = useTranslation();

  const { values } = useOrderFormik();

  const setMessage = useMessage();

  const startingCountry = values[OrderFields.STARTING_COUNTRY];

  const [ phoneCountryCode, setPhoneCountryCode ] = useState<string | null | undefined>(null);

  const { defaultLocale } = useRouter();

  const handleLockerSearchError = (error?: string) => {
    setMessage({
      message: error || t("order-message-failed_locker_search"),
      props: {
        variant: "error",
        icon: <Icon icon="warning" size="1.45rem" />,
        canClose: true,
        padding: "0.45rem 0.75rem",
      },
      zone: ORDER_MESSAGE_ZONE,
    });
  };

  useEffect(() => {
    const phoneNumber = values[OrderFields.SENDER_PHONE_NUMBER];
    const phoneNumberCode = phoneNumber ? findCountryCode(phoneNumber) : null;
    if (phoneNumberCode) {
      setPhoneCountryCode(phoneNumberCode);
    }
  }, [values[OrderFields.SENDER_PHONE_NUMBER]])

  const handleMobileOpenMapClicked = () => {
    onOpenLockersMap();
  };
  return (
    <FullWidthVStack spacing="0.5rem">
      <SectionTitle>{t("order-shipment-sender_details")}</SectionTitle>
      <FullWidthVStack spacing="1rem">
        <Box data-field={OrderFields.SENDER_NAME}>
          <TextInput
            name={OrderFields.SENDER_NAME}
            label={`${t("order-shipment-sender_name")} ${t("common-optional")}`}
            id="order-sender-name"
            maxLength={ORDER_FIELDS_MAX_LENGTHS.name}
          />
        </Box>
        <Box data-field={OrderFields.SENDER_PHONE_NUMBER}>
          <PhoneInput
            name={OrderFields.SENDER_PHONE_NUMBER}
            label={t("order-shipment-sender_mobile_number")}
            id="order-sender-phone"
            onPhoneCodeChange={setPhoneCountryCode}
            pinnedPhoneCodes={DOMAIN.DOMAIN_PHONE_NUMBERS[defaultLocale ?? ""]}
            defaultCountry={values[OrderFields.STARTING_COUNTRY]!}
          />
        </Box>
        <SenderPhoneNotificationMessage phoneCountryCode={phoneCountryCode} />
        <Box data-field={OrderFields.SENDER_EMAIL}>
          <EmailInput
            name={OrderFields.SENDER_EMAIL}
            label={t("order-shipment-sender_email")}
            id="order-sender-email"
            maxLength={ORDER_FIELDS_MAX_LENGTHS.email}
          />
        </Box>
        <LockerInput
          countryCode={startingCountry!}
          lockerCodeFieldName={OrderFields.RETURN_LOCKER}
          lockerNameFieldName={OrderFields.RETURN_LOCKER_NAME}
          lockerPostalCodeFieldName={OrderFields.SENDER_ZIP_CODE}
          lockerStreetName={OrderFields.SENDER_STREET}
          lockerCityName={OrderFields.SENDER_CITY}
          label={t("order-shipment-choose_locker_for_return")}
          isDisabled={!startingCountry}
          onError={handleLockerSearchError}
          id="order-sender-return-locker"
        />
        <Box>
          <Message align="start" icon={<Icon icon="info" size="1.25rem" />} sx={{ py: "0.7rem" }}>
            <Box>
              {t("order-shipment-you_can_take_package_to_any_locker")}
              <Mobile>
                <Link
                  variant="red"
                  isUnderlined
                  onClick={handleMobileOpenMapClicked}
                  label={t("order-shipment-choose_lockers_around_me")}
                />
              </Mobile>
            </Box>
          </Message>
        </Box>
      </FullWidthVStack>
    </FullWidthVStack>
  );
};
export default SenderDetails;
